import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Divider,
  Image,
  NumberFormatter,
  Spoiler
} from '@mantine/core';
import { ArrowLeft, ArrowRight, Photo } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import { getVendorProductExternalConnectionRestrictions } from '../../../helpers/vendorHelper';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';
import { ECOM_PRODUCT_STATUS_ENUM } from '../../../config/constants';

const ConfirmExternalConnectionProductChangeModal = ({
  isOpen,
  onClose,
  ecomVendorProducts,
  productFormStates,
  onConfirm,
  isSubmitting
}) => {
  const [viewState, setViewState] = useState({
    productIndex: 0,
    variantIndex: 0
  });

  const productRestrictions =
    ecomVendorProducts
      .filter((p) => !!p)
      ?.map((ecomVendorProduct) => {
        const restrictions = getVendorProductExternalConnectionRestrictions(
          ecomVendorProduct
        );
        const formState = productFormStates.find(
          (f) => f.uuid === ecomVendorProduct?.uuid
        );

        return formState
          ? {
              uuid: ecomVendorProduct.uuid,
              ecomVendorProduct,
              formState,
              ...restrictions,
              productChange: restrictions.getFormStateChanges(formState)
            }
          : null;
      })
      .filter((f) => !!f) || [];

  const { productChange, ecomVendorProduct } =
    productRestrictions[viewState.productIndex] || {};

  const allConfigs =
    productChange?.changeList?.reduce((r, c) => {
      c.configs.forEach((config) => {
        if (
          !r.find(
            (f) =>
              f.pkEcomExternalConnectionType ===
              config.pkEcomExternalConnectionType
          )
        ) {
          r.push(config);
        }
      });

      return r;
    }, []) || [];

  const { productChanges, variantChanges } = productChange?.changeList?.reduce(
    (r, c) => {
      if (c.type === 'product') {
        r.productChanges.push(c);
      }
      else if (c.type === 'variant') {
        r.variantChanges.push(c);
      }
      return r;
    },
    { productChanges: [], variantChanges: [] }
  ) || {
    productChanges: [],
    variantChanges: []
  };

  const productVariants = variantChanges.reduce((r, c) => {
    if (!r.find((f) => f.uuid === c.variant.uuid)) {
      const variant = ecomVendorProduct.ecomVendorProductVariants.find(
        (f) => f.uuid === c.variant.uuid
      );
      r.push({
        ...variant,
        src: variant?.previewImage,
        uuid: c.variant.uuid,
        name: c.variant.name
      });
    }
    return r;
  }, []);
  const selectedVariant = productVariants[viewState.variantIndex] || null;
  const selectedVariantChanges = variantChanges.filter(
    (f) => f.variant.uuid === selectedVariant?.uuid
  );

  useEffect(() => {
    if (isOpen) {
      setViewState({
        productIndex: 0,
        variantIndex: 0
      });
    }
  }, [isOpen]);

  const renderDisplayValue = (value, valueType, backgroundColor) =>
    valueType === 'currency' ? (
      <NumberFormatter
        decimalScale={2}
        fixedDecimalScale
        prefix="$"
        style={{ fontSize: 14, padding: '0px 8px', backgroundColor }}
        thousandSeparator
        value={value / 100}
      />
    ) : (
      <AppText
        style={{
          fontSize: 14,
          padding: '0px 8px',
          backgroundColor
        }}
      >
        {value}
      </AppText>
    );

  return (
    <ResponsiveModal
      formSectionProps={{
        padding: 0,
        cancelTitle: 'Cancel',
        onCancel: onClose,
        submitTitle: productChange?.hasChanges
          ? 'Yes, publish changes'
          : 'Save changes',
        onSubmit: onConfirm,
        isLoading: isSubmitting
      }}
      isOpen={isOpen}
      onClose={onClose}
      title="Publishing external changes"
    >
      {productChange?.hasChanges ? (
        <AppStack style={{ padding: 0, gap: 0 }}>
          <AppStack style={{ padding: 16, gap: 8 }}>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              External changes detected.
            </AppText>
            <AppText style={{ fontSize: 14 }}>
              Please review the updates below before they are published to{' '}
              {allConfigs.map((c, index) => (
                <React.Fragment key={c.pkEcomExternalConnectionType}>
                  {index > 0 && ', '}
                  <b>{c.title}</b>
                </React.Fragment>
              ))}
              .
            </AppText>
          </AppStack>
          <Divider />
          <AppFlexbox
            style={{
              padding: '8px 16px',
              gap: 8,
              backgroundColor: '#F0F0F0',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Product changes:
            </AppText>

            {productRestrictions.length > 1 && (
              <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                <AppFlexbox style={{ alignItems: 'center', gap: 5 }}>
                  <AppText style={{ fontWeight: 500 }}>
                    ({viewState.productIndex + 1}/{productRestrictions.length})
                  </AppText>
                </AppFlexbox>

                <Button.Group>
                  <Button
                    color="dark"
                    onClick={() => {
                      const prevIndex = viewState.productIndex - 1;
                      setViewState({
                        ...viewState,
                        productIndex:
                          prevIndex < 0
                            ? productRestrictions.length - 1
                            : prevIndex
                      });
                    }}
                    size="compact-sm"
                    variant="outline"
                  >
                    <ArrowLeft size={18} />
                  </Button>
                  <Button
                    color="dark"
                    onClick={() => {
                      const nextIndex = viewState.productIndex + 1;
                      setViewState({
                        ...viewState,
                        productIndex:
                          nextIndex >= productRestrictions.length
                            ? 0
                            : nextIndex
                      });
                    }}
                    size="compact-sm"
                    variant="outline"
                  >
                    <ArrowRight size={18} />
                  </Button>
                </Button.Group>
              </AppFlexbox>
            )}
          </AppFlexbox>
          <Divider />
          <AppStack style={{ padding: 16, gap: 16 }}>
            {ecomVendorProduct && (
              <AppCard
                radius={8}
                shadow="none"
                style={{ padding: 8 }}
                withBorder
              >
                <AppFlexbox style={{ gap: 8 }}>
                  <AppStack
                    style={{
                      width: 70,
                      height: 70,
                      border: 'solid 1px lightgrey',
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {ecomVendorProduct.ecomVendorProductMedia[0]?.src ? (
                      <Image
                        fit="contain"
                        h="100%"
                        src={
                          ecomVendorProduct.ecomVendorProductMedia[0]?.src ?? ''
                        }
                        w="100%"
                      />
                    ) : (
                      <Photo color="gray" size={40} />
                    )}
                  </AppStack>
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      {ecomVendorProduct.name}
                    </AppText>
                    <AppText style={{ fontSize: 13, color: '#666' }}>
                      {ecomVendorProduct.ecomVendorProductVariants.length}{' '}
                      Variants
                    </AppText>
                    {ecomVendorProduct && (
                      <AppStack style={{ marginTop: 5 }}>
                        {ecomVendorProduct.fkEcomProductStatus.toString() ===
                        ECOM_PRODUCT_STATUS_ENUM.ACTIVE ? (
                          <Badge color="green" radius="sm">
                            Active
                          </Badge>
                        ) : ecomVendorProduct.fkEcomProductStatus.toString() ===
                          ECOM_PRODUCT_STATUS_ENUM.ARCHIVED ? (
                          <Badge color="dark" radius="sm">
                            Archived
                          </Badge>
                        ) : (
                          <Badge color="gray" radius="sm">
                            Draft
                          </Badge>
                        )}
                      </AppStack>
                    )}
                  </AppStack>
                </AppFlexbox>
              </AppCard>
            )}

            {productChanges.length === 0 ? (
              <AppText style={{ fontSize: 14 }}>No changes detected.</AppText>
            ) : (
              <>
                {productChanges.map((c, index) => (
                  <React.Fragment key={c.key}>
                    {index > 0 && <Divider />}
                    <AppStack style={{ gap: 8 }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        {c.title}
                      </AppText>
                      <Spoiler
                        hideLabel="Hide"
                        maxHeight={120}
                        showLabel="Show more"
                      >
                        <AppFlexbox style={{ flex: 1, gap: 16 }}>
                          <AppFlexbox style={{ maxWidth: '48%' }}>
                            {renderDisplayValue(
                              c.previousValue,
                              c.valueType,
                              'rgba(196, 0, 0, 0.4)'
                            )}
                          </AppFlexbox>

                          <AppStack style={{ maxWidth: '4%' }}>
                            <ArrowRight size={18} />
                          </AppStack>

                          <AppFlexbox style={{ maxWidth: '48%' }}>
                            {renderDisplayValue(
                              c.newValue,
                              c.valueType,
                              'rgba(6, 125, 98,0.4)'
                            )}
                          </AppFlexbox>
                        </AppFlexbox>
                      </Spoiler>
                    </AppStack>
                  </React.Fragment>
                ))}
              </>
            )}
          </AppStack>
          <Divider />
          <AppFlexbox
            style={{
              padding: '8px 16px',
              gap: 8,
              backgroundColor: '#F0F0F0',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Variant changes:
            </AppText>

            {productVariants.length > 1 && (
              <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                <AppFlexbox style={{ alignItems: 'center', gap: 5 }}>
                  <AppText style={{ fontWeight: 500 }}>
                    ({viewState.variantIndex + 1}/{productVariants.length})
                  </AppText>
                </AppFlexbox>

                <Button.Group>
                  <Button
                    color="dark"
                    onClick={() => {
                      const prevIndex = viewState.variantIndex - 1;
                      setViewState({
                        ...viewState,
                        variantIndex:
                          prevIndex < 0 ? productVariants.length - 1 : prevIndex
                      });
                    }}
                    size="compact-sm"
                    variant="outline"
                  >
                    <ArrowLeft size={18} />
                  </Button>
                  <Button
                    color="dark"
                    onClick={() => {
                      const nextIndex = viewState.variantIndex + 1;
                      setViewState({
                        ...viewState,
                        variantIndex:
                          nextIndex >= productVariants.length ? 0 : nextIndex
                      });
                    }}
                    size="compact-sm"
                    variant="outline"
                  >
                    <ArrowRight size={18} />
                  </Button>
                </Button.Group>
              </AppFlexbox>
            )}
          </AppFlexbox>
          <Divider />
          <AppStack style={{ padding: 16, gap: 16 }}>
            {selectedVariant && (
              <AppCard
                radius={8}
                shadow="none"
                style={{ padding: 8 }}
                withBorder
              >
                <AppFlexbox style={{ gap: 8 }}>
                  <AppStack
                    style={{
                      width: 70,
                      height: 70,
                      border: 'solid 1px lightgrey',
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {selectedVariant.src ? (
                      <Image
                        fit="contain"
                        h="100%"
                        src={selectedVariant.src ?? ''}
                        w="100%"
                      />
                    ) : (
                      <Photo color="gray" size={40} />
                    )}
                  </AppStack>
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      {selectedVariant?.ecomVendorProductVariantOptions
                        ?.sort((a, b) => a.nameSort - b.nameSort)
                        .map((s) => s.value)
                        .join(' / ') ||
                        `Variant #${viewState.variantIndex + 1}`}
                    </AppText>
                  </AppStack>
                </AppFlexbox>
              </AppCard>
            )}

            {variantChanges.length === 0 ? (
              <AppText style={{ fontSize: 14 }}>No changes detected.</AppText>
            ) : (
              selectedVariantChanges.map((c, index) => (
                <React.Fragment key={c.key}>
                  {index > 0 && <Divider />}
                  <AppStack style={{ gap: 8 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      {c.title}
                    </AppText>
                    <Spoiler
                      hideLabel="Hide"
                      maxHeight={120}
                      showLabel="Show more"
                    >
                      <AppFlexbox style={{ flex: 1, gap: 16 }}>
                        <AppFlexbox style={{ maxWidth: '48%' }}>
                          {renderDisplayValue(
                            c.previousValue,
                            c.valueType,
                            'rgba(196, 0, 0, 0.4)'
                          )}
                        </AppFlexbox>

                        <AppStack style={{ maxWidth: '4%' }}>
                          <ArrowRight size={18} />
                        </AppStack>

                        <AppFlexbox style={{ maxWidth: '48%' }}>
                          {renderDisplayValue(
                            c.newValue,
                            c.valueType,
                            'rgba(6, 125, 98,0.4)'
                          )}
                        </AppFlexbox>
                      </AppFlexbox>
                    </Spoiler>
                  </AppStack>
                </React.Fragment>
              ))
            )}
          </AppStack>
        </AppStack>
      ) : (
        <AppStack style={{ padding: 16, gap: 8 }}>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            No Changes Detected.
          </AppText>

          <AppText style={{ fontSize: 14 }}>
            There are no product updates that need to be published externally.
          </AppText>

          <AppText style={{ fontSize: 14 }}>You're all set to save.</AppText>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

ConfirmExternalConnectionProductChangeModal.propTypes = {
  ecomVendorProducts: PropTypes.array,
  isOpen: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  productFormStates: PropTypes.array
};

export default ConfirmExternalConnectionProductChangeModal;
